/**
 * Security checks for user-authored LiquidJS templates.
 *
 * Blocks template strings that contain the seed pattern of the LiquidJS v9
 * prototype-pollution → RCE chain (Dependabot alert #1819):
 *
 *   `{{ 1 | valueOf }}` returns the JS Number prototype on v9, which lets an
 *   attacker chain `| constructor` and friends to escape the template sandbox
 *   and execute arbitrary code.
 *
 * A production scan (`packages/scripts/scan-liquid-valueof-rce.sql`) against
 * every prod table that stores user-authored Liquid (1.1B+ historical
 * sync_requests included) returned zero hits, so blocking this pattern at
 * save time breaks no existing customer template.
 *
 * This module is pure (no I/O, no logger) so it can be imported from both
 * the backend renderer and the frontend save-time validator. Backend callers
 * that want loud telemetry should catch `LiquidSecurityError` and report it
 * via their own logger / Sentry path.
 */

/**
 * Pipe followed by optional whitespace and the literal identifier `valueOf`.
 * This is the seed of the v9 RCE chain. Case-sensitive — the JS identifier
 * is `valueOf` and Liquid filter names are case-sensitive, so a case-insensitive
 * match would only produce false positives on benign substrings like `valueof_id`.
 */
export const LIQUID_RCE_VALUEOF_PATTERN = /\|\s*valueOf\b/;

/**
 * Error thrown when a template contains a forbidden security pattern.
 *
 * Tagged with `name = "LiquidSecurityError"` so callers (GraphQL error
 * formatters, Express middleware) can identify it across rethrow boundaries
 * without instanceof checks failing under bundler duplication.
 */
export class LiquidSecurityError extends Error {
  override readonly name = "LiquidSecurityError";
  readonly pattern: string;
  readonly snippet: string;

  constructor(pattern: string, snippet: string) {
    // Message is intentionally vague — anything that surfaces to the user
    // must not name the offending pattern, since that would teach an
    // attacker what to try next. Forensic detail lives in `pattern` and
    // `snippet` for logger/Sentry capture.
    super(
      "This template was rejected. Please contact support for more information.",
    );
    this.pattern = pattern;
    this.snippet = snippet;
  }
}

/**
 * Returns true if the template contains the `| valueOf` RCE seed pattern.
 */
export function containsLiquidRcePattern(template: string): boolean {
  return LIQUID_RCE_VALUEOF_PATTERN.test(template);
}

/**
 * Throws `LiquidSecurityError` if the template contains a forbidden pattern.
 *
 * Pure — does not log. Backend callers should catch and report via their
 * own logger / Sentry path so the alert lands in Datadog with the right tags.
 */
export function assertLiquidTemplateIsSafe(template: string): void {
  const match = template.match(LIQUID_RCE_VALUEOF_PATTERN);
  if (match) {
    const start = Math.max(0, (match.index ?? 0) - 40);
    const end = Math.min(
      template.length,
      (match.index ?? 0) + match[0].length + 40,
    );
    throw new LiquidSecurityError(
      LIQUID_RCE_VALUEOF_PATTERN.source,
      template.slice(start, end),
    );
  }
}
